import { getConsolidatedTranslations } from 'core/utils/parsers';
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	Box,
	Divider,
	Grid,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Tooltip,
	Typography,
} from '@mui/material';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';

import { format, getLocalTimeFromUTC } from '../../utils/converters';
import { getRegularPriceValue, REGULAR_PRICE_TYPE } from '../../utils/prices';

const iconHUA = {
	'detected_products': 'stock',
	'stock': 'bag-check-outline',
	'stockout': 'stock-out',
	'price_difference': 'price-difference',
	'spread_to_fill': 'grid-outline',
	'seen_on_day': 'calendar-outline',
}

export const getPriceRows = (
	prices,
	{ currencySymbol, priceLabel, posPriceLabel, translatePriceType },
) =>
	Object.fromEntries(
		prices
			.filter(priceEntry => priceEntry.price_type !== REGULAR_PRICE_TYPE)
			.flatMap(priceEntry => {
				const priceTypeQualifier = ` (${translatePriceType(priceEntry.price_type)})`;
				return [
					[
						`price_${priceEntry.price_type}`,
						{
							data: true,
							icon: 'pricetag',
							primary: priceLabel + priceTypeQualifier,
							secondary: currencySymbol + priceEntry.price,
						},
					],
					[
						`pos_price_${priceEntry.price_type}`,
						{
							data: true,
							icon: 'pricetag',
							primary: posPriceLabel + priceTypeQualifier,
							secondary: currencySymbol + priceEntry.pos_price,
						},
					],
				];
			}),
	);

export default function CustomCardContent(props) {
	const {
		selectedProduct,
		selectedStore,
		dateState,
		currencySymbol,
		clientFormats,
		alertsIcons,
	} = props;

	const { t, i18n } = useTranslation();

	const [productInfo, setProductInfo] = useState(null);
	const cardDataHUA = ['detected_products', 'stock', 'stockout', 'price_difference', 'spread_to_fill', 'seen_on_day'];

	// Parametrize the product info
	useEffect(() => {
		if (selectedProduct && selectedProduct?.texts) {
			const seenOnDay = format(dateState, i18n.language, clientFormats?.date_format);

			// "regular" and "promotional" are only translated in the digital store dictionary.
			const translatePriceType = priceType =>
				getConsolidatedTranslations(t, selectedStore?.chain_name, priceType, 'digital_store', priceType);
			const prices = Array.isArray(selectedProduct?.prices)
				? selectedProduct.prices
				: [];
			const priceLabel = getConsolidatedTranslations(t, selectedStore?.chain_name, 'price', 'Walls', 'Price');
			const posPriceLabel = t('cws_app.Walls.pos_price', 'POS Price');
			const regularPrice = getRegularPriceValue(selectedProduct, 'price');
			const regularPosPrice = getRegularPriceValue(selectedProduct, 'pos_price');

			setProductInfo({
				alerts: { data: selectedProduct.hasOwnProperty('stockout'), icon: 'information-circle', secondary: alertsIcons, primary: t('cws_app.general.status', 'Status') },
				price: { data: regularPrice > 0, icon: 'pricetag', secondary: currencySymbol + regularPrice, primary: priceLabel },
				pos_price: { data: regularPosPrice, icon: 'pricetag', secondary: currencySymbol + regularPosPrice, primary: posPriceLabel },
				...getPriceRows(prices, { currencySymbol, priceLabel, posPriceLabel, translatePriceType }),
				ean: { data: selectedProduct?.ean.toString().length > 1, icon: 'barcode-outline', secondary: selectedProduct?.ean, primary: getConsolidatedTranslations(t, selectedStore?.chain_name, 'ean', 'Walls', 'Barcode') },
				item: { data: selectedProduct?.item.toString().length > 1, icon: 'disc-outline', secondary: selectedProduct?.item, primary: getConsolidatedTranslations(t, selectedStore?.chain_name, 'item', 'Walls', 'SKU') },
				name_category: { data: selectedProduct?.name_category, icon: 'apps-outline', secondary: selectedProduct?.name_category, primary: t('cws_app.general.sel_category') },
				seen_on_day: { data: seenOnDay, icon: 'calendar-outline', secondary: seenOnDay, primary: t('cws_app.Walls.seen_on_day') },
				landmark: { data: selectedProduct?.landmark, icon: 'location', secondary: selectedProduct?.landmark, primary: getConsolidatedTranslations(t, selectedStore?.chain_name, 'landmark', 'Walls', 'Landmark') },
				price_update_timestamp: { data: selectedProduct?.price_update_timestamp, icon: 'calendar-outline', secondary: selectedProduct?.price_update_timestamp ? format(getLocalTimeFromUTC(selectedProduct?.price_update_timestamp).localDate, i18n.language, 'dd/MM/yyyy HH:mm') : '-', primary: t('cws_app.Walls.price_update_timestamp', 'Price Update'), tooltip: Intl.DateTimeFormat().resolvedOptions().timeZone },
				stock_available: { data: selectedProduct?.stock_available, icon: 'bag-check-outline', secondary: selectedProduct?.stock_available, primary: t('cws_app.Walls.stock_available', 'Stock Available') },
				task_stock_available: { data: selectedProduct?.task_stock_available, icon: 'bag-check-outline', secondary: selectedProduct?.task_stock_available, primary: t('cws_app.Walls.task_stock_available', 'Task Stock Available') },
				stock_update_timestamp: { data: selectedProduct?.stock_update_timestamp, icon: 'calendar-outline', secondary: selectedProduct?.stock_update_timestamp ? format(getLocalTimeFromUTC(selectedProduct?.stock_update_timestamp).localDate, i18n.language, 'dd/MM/yyyy HH:mm') : '-', primary: t('cws_app.Walls.stock_update_timestamp', 'Stock Update'), tooltip: Intl.DateTimeFormat().resolvedOptions().timeZone },
				promotion_info: (selectedProduct?.promotion_type || selectedProduct?.catalog) ?
					{
						catalog: { icon: "apps-outline", title: getConsolidatedTranslations(t, selectedStore?.chain_name, 'store_catalog', 'Walls', 'store_catalog'), info: selectedProduct?.catalog },
						status: { icon: "information-circle", title: t('cws_app.Walls.promo_status', 'Promotion Status'), info: selectedProduct?.active ? 'Active' : 'Inactive' },
						promotion_type: { icon: "apps-outline", title: t('cws_app.Walls.promotion_type', 'Promotion Type'), info: selectedProduct?.promotion_type },
						promotion_value: { icon: "pricetag", title: t('cws_app.Walls.promotion_value', 'Promotion Value'), info: selectedProduct?.promotion_value },
						start_date: { icon: "calendar-outline", title: t('cws_app.Walls.start_date'), info: format(new Date(selectedProduct?.start_date ?? null), i18n.language, clientFormats?.date_format) },
						end_date: { icon: "calendar-outline", title: t('cws_app.Walls.end_date'), info: format(new Date(selectedProduct?.end_date ?? null), i18n.language, clientFormats?.date_format) }
					} : null
			});
		}
		else if (selectedProduct?.promotion_type || selectedProduct?.catalog) {
			setProductInfo({
				promotion_info: {
					catalog: { icon: "apps-outline", title: getConsolidatedTranslations(t, selectedStore?.chain_name, 'store_catalog', 'Walls', 'store_catalog'), info: selectedProduct?.catalog },
					status: { icon: "information-circle", title: t('cws_app.Walls.promo_status', 'Promotion Status'), info: selectedProduct?.active ? 'Active' : 'Inactive' },
					promotion_type: { icon: "apps-outline", title: t('cws_app.Walls.promotion_type', 'Promotion Type'), info: selectedProduct?.promotion_type },
					promotion_value: { icon: "pricetag", title: t('cws_app.Walls.promotion_value', 'Promotion Value'), info: selectedProduct?.promotion_value },
					start_date: { icon: "calendar-outline", title: t('cws_app.Walls.start_date'), info: format(new Date(selectedProduct?.start_date ?? null), i18n.language, clientFormats?.date_format) },
					end_date: { icon: "calendar-outline", title: t('cws_app.Walls.end_date'), info: format(new Date(selectedProduct?.end_date ?? null), i18n.language, clientFormats?.date_format) }
				}
			})
		}
	}, [alertsIcons, selectedProduct])

	return (
		<Box sx={{ width: '100%' }}>
			{(selectedProduct?.texts || productInfo?.promotion_info) ?
				<Grid container alignItems={'center'}>
					<Grid item xs={12} size={12} justifyContent={'center'} alignItems={'center'} display={'flex'}>
						<List dense sx={styles.rootList}>
							<Box sx={[styles.boxList]}>
								{productInfo && Object.keys(productInfo).map((item, idx) => {
									if (item === 'promotion_info') {
										return (
											productInfo && productInfo?.promotion_info && Object.keys(productInfo?.promotion_info).map((item, idx) => (
												<ListItem
													secondaryAction={<Typography>{productInfo.promotion_info[item].info ? productInfo.promotion_info[item].info : '-'}</Typography>}
												>
													<ListItemAvatar sx={(theme) => { return { ...styles.avatar, color: theme.palette.secondary.main } }}> <IconComponent iconName={productInfo.promotion_info[item].icon} color="secondary" style={{ ...styles.icon, color: 'inherit' }} height={'1.3em'} /> </ListItemAvatar>
													<ListItemText
														primary={productInfo.promotion_info[item].title}
													/>
												</ListItem>))
										)
									}
									return (
										<div key={idx}>
											{(productInfo[item].data !== null && productInfo[item].data !== undefined && productInfo[item].data !== false) ?
												<ListItem
													key={item + '-' + idx}
													sx={styles.listItem}
													secondaryAction={<Tooltip title={productInfo[item].tooltip} arrow><Typography variant='body2' >{productInfo[item].secondary}</Typography></Tooltip>}
												>
													<ListItemAvatar sx={(theme) => { return { ...styles.avatar, color: theme.palette.secondary.main } }}> <IconComponent iconName={productInfo[item].icon} style={{ ...styles.icon, color: 'inherit' }} /> </ListItemAvatar>
													<ListItemText
														primary={<Typography variant='body2' sx={{ fontWeight: 500 }}>{productInfo[item].primary}</Typography>}
													/>
												</ListItem>
												: null
											}
										</div>)
								})}
							</Box>
						</List>
					</Grid>
				</Grid>

				: selectedProduct?.data ?
					<Grid container alignItems={'center'}>
						<List dense sx={styles.rootList}>
							<ListItem
								sx={[styles.listItemLandmark, { my: 0 }]}
							>
								<ListItemAvatar sx={(theme) => { return { ...styles.avatar, color: theme.palette.secondary.main } }}> <IconComponent iconName={'location'} style={{ ...styles.icon, color: 'inherit' }} height={'1.3em'} /> </ListItemAvatar>
								<ListItemText
									primary={
										<Typography sx={{ fontWeight: 500 }}>
											{getConsolidatedTranslations(t, selectedStore?.chain_name, 'landmark', 'Walls', 'landmark')
												+ ': ' + t(`cws_app.Walls.${selectedProduct.landmark_type}`, selectedProduct.landmark_type)}
										</Typography>
									}
								/>
							</ListItem>
							<Divider sx={styles.divider} />
							<Box sx={[styles.boxList]}>
								{cardDataHUA.map((item, idx) =>
									<ListItem key={item + '-' + idx} sx={styles.listItem}
										secondaryAction={<Typography>{
											item === 'seen_on_day' ? format(dateState, i18n.language, clientFormats?.date_format)
												: item === 'detected_products' ?
													selectedProduct?.data['total']
													: selectedProduct?.data[item.toLowerCase()]
										}
										</Typography>
										}
									>
										<ListItemAvatar sx={(theme) => { return { ...styles.avatar, color: theme.palette.secondary.main } }}><IconComponent iconName={iconHUA[item]} style={{ ...styles.icon, color: 'inherit' }} height={'1.2em'} /></ListItemAvatar>
										<ListItemText primary={t('cws_app.Walls.' + item.toLowerCase(), item)} />
									</ListItem>
								)}
							</Box>
						</List>
					</Grid>

					: selectedProduct?.promotion_type ?
						<Grid container>
							<List dense sx={styles.rootList}>
								{productInfo && productInfo?.promotion_info && Object.keys(productInfo?.promotion_info).map((item, idx) => (
									<ListItem
										secondaryAction={<Typography>{productInfo.promotion_info[item].info}</Typography>}
									>
										<ListItemAvatar sx={(theme) => { return { ...styles.avatar, color: theme.palette.secondary.main } }}> <IconComponent iconName={productInfo.promotion_info[item].icon} color="secondary" style={{ ...styles.icon, color: 'inherit' }} height={'1.3em'} /> </ListItemAvatar>
										<ListItemText
											primary={productInfo.promotion_info[item].title}
										/>
									</ListItem>)
								)}
							</List>
						</Grid>
						: <Grid container>
							<Box sx={{
								width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center',
								alignItems: 'center',
								height: '15em',
								border: '1px solid #c7c7d754',
								borderRadius: '8px',
								alignContent: 'center',
							}}>
								<Typography variant='body1' sx={{ color: '#d3d3d3cc', ...styles.bottomText }}>{t('cws_app.Walls.no_item_selected', "No item selected")}</Typography>
							</Box>
						</Grid>
			}
		</Box >
	)
}


const styles = {
	rootList: {
		minWidth: '90%!important',
		maxHeight: '20em',
		border: '1px solid #0000001f',
		borderTop: '0px',
		overflow: 'auto',
	},
	divider: {
		borderTop: '1px solid #ffffff52',
		borderRadius: 1,
		width: '100%',
	},
	title: {
		fontSize: 10,
		fontWeight: 'bold',
	},
	body: {
		fontSize: 8,
	},
	bottomText: {
		fontSize: '14px',
		mb: 1
	},
	icon: {
		fontSize: 20,
		mx: 1
	},
	listItemLandmark: {
		width: 300,
	},
	listItem: {
		minWidth: '18em',
	},
	boxList: {
		width: '100%!important',
		display: 'flex',
		flexFlow: 'column wrap',
		gap: "0 0px",
		overflow: 'auto',
		my: 0,
	},
	secondaryText: { color: '#ffffffc2' },
	avatar: {
		minWidth: '2em',
	}
}
